import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { toaster } from "@/components/ui/toaster";
import { RequestTypes } from "@/constants/enums";
import { ServiceRequestStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { SolicitudVoleoDetail } from "@/services/voleo-requests.service";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { AttachPrescriptionModal } from "../create/AttachPrescriptionModal";
import { VoleoRequestFileCard } from "./components/VoleoRequestFileCard";
import { VoleoRequestMainData } from "./components/VoleoRequestMainData";
import { VoleoRequestNotes } from "./components/VoleoRequestNotes";
import { VoleoRequestActionButtons } from "./VoleoRequestActionButtons";
import {
  attachVoleoRequestFile,
  closeVoleoRequest,
  fetchVoleoRequestDetail,
  markDoneVoleoRequest,
  rejectVoleoRequest,
  replaceVoleoRequestFileUpload,
} from "./handler-functions";

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el.RejectRequestModal,
  })),
);
const MarkDoneSamplingRequestModalDynamic = lazy(() =>
  import("@/components/modal/MarkDoneSamplingRequestModal").then((el) => ({
    default: el.MarkDoneSamplingRequestModal,
  })),
);

export const VoleoRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions, hasWriteAccess } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detail, setDetail] = useState<SolicitudVoleoDetail | null>(null);

  const { open: openRejectModal, setOpen: setOpenRejectModal } = useDisclosure();
  const [loadingReject, setLoadingReject] = useState(false);

  const { open: openDoneModal, setOpen: setOpenDoneModal } = useDisclosure();
  const [loadingDone, setLoadingDone] = useState(false);

  const [loadingClose, setLoadingClose] = useState(false);

  const [openAttachModal, setOpenAttachModal] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const handleBack = () => {
    navigate(AppRoutes.VOLEO_REQUESTS);
  };

  const fetchDetail = async (showLoad = true) =>
    fetchVoleoRequestDetail({
      id,
      showLoad,
      setLoadingDetail,
      setDetail,
      navigate,
      fetchPermissions,
    });

  useEffect(() => {
    if (isActive && id) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, id]);

  const isRejected = detail?.estado === ServiceRequestStatus.REJECTED;
  const isDone = detail?.estado === ServiceRequestStatus.DONE;
  const isClosed = detail?.estado === ServiceRequestStatus.CLOSED;
  const isPending = detail?.estado === ServiceRequestStatus.PENDING;

  const handleRejectRequest = (event?: FieldValues) =>
    rejectVoleoRequest({
      detailId: detail?.id,
      idParam: id,
      event,
      setLoadingReject,
      setOpenRejectModal,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const handleMarkDone = (values: { quantity: number; fechaRealizacion: string }) =>
    markDoneVoleoRequest({
      detailId: detail?.id,
      idParam: id,
      values,
      setLoadingDone,
      setOpenDoneModal,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const handleCloseRequest = () =>
    closeVoleoRequest({
      detailId: detail?.id,
      idParam: id,
      setLoadingClose,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const handleAttachOrReplaceAccept = async (file?: File) => {
    if (!file) {
      toaster.create({
        type: "warning",
        title: "Seleccioná un archivo .zip para continuar.",
      });
      return;
    }

    if (detail?.idArchivoAdjunto) {
      await replaceVoleoRequestFileUpload({
        detailId: detail?.id,
        idParam: id,
        idArchivoAdjunto: detail?.idArchivoAdjunto,
        file,
        setLoadingFile,
        navigate,
        fetchPermissions,
        fetchDetail,
      });
    } else {
      await attachVoleoRequestFile({
        detailId: detail?.id,
        idParam: id,
        file,
        setLoadingFile,
        navigate,
        fetchPermissions,
        fetchDetail,
      });
    }
  };

  const showExecutionInfo = isDone || isClosed;

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px" height="100%" minHeight={0}>
      <SectionHeader
        title="Solicitud de voleo"
        onBack={handleBack}
        backButton
        loading={loadingDetail || loadingReject || loadingDone || loadingClose}
      />

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="12px"
          paddingRight="8px"
          paddingBottom="20px"
        >
          <VoleoRequestActionButtons
            detail={detail}
            hasWriteAccess={hasWriteAccess}
            loadingReject={loadingReject}
            loadingDone={loadingDone}
            loadingClose={loadingClose}
            setOpenDoneModal={setOpenDoneModal}
            setOpenRejectModal={setOpenRejectModal}
            handleCloseRequest={handleCloseRequest}
          />

          {detail && (
            <VoleoRequestMainData detail={detail} showExecutionInfo={showExecutionInfo} />
          )}

          <VoleoRequestFileCard
            idArchivoAdjunto={detail?.idArchivoAdjunto}
            nombreArchivo={detail?.nombreArchivo}
            urlArchivo={detail?.urlArchivo}
            archivoDisponible={detail?.archivoDisponible}
            canWrite={isPending && Boolean(hasWriteAccess(PermissionCode.BROADCAST_REQUEST))}
            loading={loadingFile}
            onAttach={() => setOpenAttachModal(true)}
            onReplace={() => setOpenAttachModal(true)}
          />

          <VoleoRequestNotes detail={detail} isRejected={isRejected} />

          <AttachPrescriptionModal
            open={openAttachModal}
            setOpen={setOpenAttachModal}
            onAccept={handleAttachOrReplaceAccept}
          />

          <Suspense>
            <RejectRequestModalDynamic
              open={openRejectModal}
              setOpen={setOpenRejectModal}
              handleAccept={handleRejectRequest}
              loadingAccept={loadingReject}
              idTipoSolicitud={RequestTypes.SolicitudVoleo}
              entityName="solicitud"
            />
            <MarkDoneSamplingRequestModalDynamic
              open={openDoneModal}
              setOpen={setOpenDoneModal}
              quantityLabel="hectáreas"
              defaultQuantity={detail?.hectareas}
              allowDecimal
              handleAccept={handleMarkDone}
              loadingAccept={loadingDone}
              isMuestrasUnidad={false}
            />
          </Suspense>
        </Flex>
      )}
    </Flex>
  );
};
